import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateSpaceData, useCreateSpaceV2 } from '@zep/apis';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { SpaceUsageType } from '@zep/types';

import { CreateSpaceSchemaType } from '../schema';

interface UseCreateSpaceOptions {
  onSuccess?: (spaceId: string, entryMapHashId: string) => void;
  analyticsEvent?: string;
  place?: 'play' | 'home';
}

export function useCreateSpace(options: UseCreateSpaceOptions = {}) {
  const { t } = useTranslation(['common']);
  const dialog = useDialog();
  const createSpaceMutation = useCreateSpaceV2();

  const createSpace = useCallback(
    async (
      data: CreateSpaceSchemaType,
      template: { hashId: string; name: string; isPurchased?: boolean },
    ) => {
      try {
        // 요청 객체 생성
        const request: CreateSpaceData = {
          name: data.name.trim(),
          spaceUsageType: data.spaceUsageType,
          enablePassword: false,
          enableRtc: data.enableRtc,
        };

        // 비밀번호 설정
        if (data.enablePassword && data.password?.trim().length) {
          request.enablePassword = true;
          request.password = data.password;
        }

        // 템플릿 설정
        if (template.isPurchased) {
          request.purchasedTemplateHashId = template.hashId;
        } else {
          request.templateHashId = template.hashId;
        }

        // 스페이스 생성 API 호출
        const space = await createSpaceMutation.mutateAsync(request);

        // Analytics 이벤트
        if (options.analyticsEvent) {
          const eventProps = {
            map_template_id: template.hashId,
            template_name: template.name,
            place: options.place || 'play',
            space_name: space.name,
            spaceUsageType:
              space.options.spaceUsageType === SpaceUsageType.PRIVATE
                ? 'private'
                : 'public',
            password: space.options.enablePassword ? 'true' : 'false',
            category: space.categoryIds.map(String),
            rtc: data.enableRtc ? 'on' : 'off',
          };

          logger.track(options.analyticsEvent, eventProps);
        }

        // 성공 콜백 호출
        if (options.onSuccess) {
          options.onSuccess(space.hashId, space.entryMapHashId);
        }

        return space;
      } catch (error) {
        console.error('Failed to create space:', error);
        await dialog.alert({
          title: t('common:error.api.unknown'),
        });
        throw error;
      }
    },
    [
      createSpaceMutation,
      dialog,
      options.analyticsEvent,
      options.onSuccess,
      options.place,
      t,
    ],
  );

  return {
    createSpace,
    isLoading: createSpaceMutation.isLoading,
  };
}
